import { atom } from 'jotai'
import { addEdge, Edge } from '@xyflow/react'

import { getNodeHandleId } from 'Automations/nodeDefinitions/helpers/nodeHandleId'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { presentationAtom } from './presentation.atoms'
import { getResolvedNodeDefinitions } from 'Automations/nodeDefinitions/resolvedNodeDefinitions.atoms'
import {
  collapsedNodeSizesAtom,
  expandedNodeSizesAtom,
  expandedNodeTypesAtom
} from './nodeExpansion.atoms'
import { AutomationsPresentationV1Node } from './types'
import { CONNECTION_PREVIEW_NODE_ID } from './components/Canvas/components/ReactFlow/connectionPreview.constants'

/**
 * Set when a connection is dragged from a source handle and dropped on empty
 * canvas. Drives the connection-drop node picker and (with the preview type
 * below) the in-canvas preview.
 */
export const connectionDropAtom = atom<{
  sourceNodeId: string
  sourceHandleId: string | null
  /** Flow-coordinate drop point — where the new node is placed. */
  position: { x: number; y: number }
  /** Viewport-coordinate drop point — where the picker is anchored. */
  clientX: number
  clientY: number
} | null>(null)

/** The node type currently hovered in the connection-drop picker, previewed in canvas. */
export const connectionDropPreviewTypeAtom = atom<NodeType | null>(null)

/**
 * The ghost preview node (the picked node type at the drop point) while a type
 * is hovered. `useNode` returns this for the preview id so the real editor node
 * renders with default config without being a real presentation node.
 */
export const connectionDropPreviewNodeAtom = atom<
  AutomationsPresentationV1Node | undefined
>(get => {
  const drop = get(connectionDropAtom)
  const previewType = get(connectionDropPreviewTypeAtom)
  if (!drop || !previewType) return undefined

  const definitions = getResolvedNodeDefinitions(get)

  return {
    // Type-specific id so React Flow remounts the correct node component when
    // the hovered type changes (it doesn't swap components for an in-place
    // type change, which would feed the old component mismatched data).
    id: `${CONNECTION_PREVIEW_NODE_ID}:${previewType}`,
    type: previewType,
    position: drop.position,
    data: { ...definitions[previewType].defaultConfig }
  }
})

/**
 * Create the picked node at the drop point and connect the dragged source
 * handle to it, then clear the picker/preview state.
 */
export const addNodeFromConnectionAtom = atom(
  null,
  (
    get,
    set,
    params: { nodeType: NodeType; config?: Record<string, unknown> }
  ) => {
    const drop = get(connectionDropAtom)

    set(connectionDropAtom, null)
    set(connectionDropPreviewTypeAtom, null)

    if (!drop) return

    const presentation = get(presentationAtom)
    if (!presentation) return

    const definitions = getResolvedNodeDefinitions(get)

    const { nodeType, config } = params
    const newNodeId = crypto.randomUUID()
    const data = {
      ...definitions[nodeType].defaultConfig,
      ...(config ?? {})
    }

    const maxZIndex = presentation.nodes.reduce(
      (max, node) => Math.max(max, (node as any).zIndex ?? 0),
      0
    )

    // If the new node's type is currently expanded it's "born expanded", and the
    // expansion layout centres an expanded node on its collapsed position —
    // shifting it by -Δsize/2 from its stored position. That would land it far
    // from the drop point (and its ghost), since the ghost isn't laid out.
    // Compensate by storing the position +Δsize/2 so the rendered (offset) node
    // sits exactly where it was dropped, and seed its sizes from a same-type
    // sibling so the offset applies on the first frame (no jump).
    const position = ((): { x: number; y: number } => {
      if (!get(expandedNodeTypesAtom).has(nodeType)) return drop.position

      const collapsedSizes = get(collapsedNodeSizesAtom)
      const expandedSizes = get(expandedNodeSizesAtom)
      const sibling = presentation.nodes.find(
        node =>
          node.type === nodeType &&
          collapsedSizes[node.id] &&
          expandedSizes[node.id]
      )
      if (!sibling) return drop.position

      const collapsed = collapsedSizes[sibling.id]
      const expanded = expandedSizes[sibling.id]
      set(collapsedNodeSizesAtom, prev => ({ ...prev, [newNodeId]: collapsed }))
      set(expandedNodeSizesAtom, prev => ({ ...prev, [newNodeId]: expanded }))

      return {
        x: drop.position.x + (expanded.width - collapsed.width) / 2,
        y: drop.position.y + (expanded.height - collapsed.height) / 2
      }
    })()

    const newNode = {
      id: newNodeId,
      type: nodeType,
      position,
      data,
      zIndex: maxZIndex + 1
    } as AutomationsPresentationV1Node

    const edges = addEdge(
      {
        source: drop.sourceNodeId,
        sourceHandle: drop.sourceHandleId,
        target: newNodeId,
        targetHandle: getNodeHandleId({ nodeId: newNodeId, type: 'target' })
      },
      presentation.edges as Edge[]
    )

    set(presentationAtom, {
      ...presentation,
      nodes: [...presentation.nodes, newNode],
      edges
    })
  }
)
